import {FullScreenSpinner} from '@/components/ui/spinner';
import {auth, useAuthState} from '@accumulus/shared';
import Layout from '@/components/sidebar';
import {useEmployeeRole} from '@/stores/employee';
import {
  canDrawSample,
  canManageEmployees,
  canOperate,
  canSimulate,
  canValidate,
  canViewAgentVersions,
  canViewDashboard,
  getHomePath,
  type TRole
} from '@/lib/kbi-roles';

import {
  type RouteObject,
  createBrowserRouter,
  Navigate,
  Outlet
} from 'react-router-dom';
import {lazy, Suspense} from 'react';
import SignInPage from '@/pages/sign-in';
import ForgotPasswordPage from '@/pages/forgot-password';
import NotFoundPage from '@/pages/not-found';

const OnboardingAdminPage = lazy(() => import('@/pages/onboarding-admin'));
const AirlinesPage = lazy(() => import('@/pages/airlines'));
const AgentExecutionListPage = lazy(
  () => import('@/pages/agent-execution/list')
);
const AgentExecutionDetailPage = lazy(
  () => import('@/pages/agent-execution/detail')
);
const TNAgentExecutionListPage = lazy(
  () => import('@/pages/tn-agent-execution/list')
);
const TNAgentExecutionNegotiationsPage = lazy(
  () => import('@/pages/tn-agent-execution/negotiations')
);
const TNAgentExecutionDetailPage = lazy(
  () => import('@/pages/tn-agent-execution/detail')
);
const TNSimulationListPage = lazy(() => import('@/pages/tn-simulation/list'));
const TNSimulationDetailPage = lazy(
  () => import('@/pages/tn-simulation/detail')
);
const FlightSimulatorListPage = lazy(
  () => import('@/pages/flight-simulator/list')
);
const FlightSimulatorNewRunPage = lazy(
  () => import('@/pages/flight-simulator/new')
);
const FlightSimulatorRunPage = lazy(
  () => import('@/pages/flight-simulator/run')
);
const FlightSimulatorGuidePage = lazy(
  () => import('@/pages/flight-simulator/guide')
);
const RcpScenarioListPage = lazy(() => import('@/pages/rcp-scenario/list'));
const RcpScenarioNewPage = lazy(() => import('@/pages/rcp-scenario/new'));
const RcpScenarioDetailPage = lazy(() => import('@/pages/rcp-scenario/detail'));
const EventListPage = lazy(() => import('@/pages/event/list'));
const EventDetailPage = lazy(() => import('@/pages/event/detail'));
const QuestionnaireListPage = lazy(() => import('@/pages/questionnaire/list'));
const QuestionnaireDetailPage = lazy(
  () => import('@/pages/questionnaire/detail')
);
const QuestionnaireResultsPage = lazy(
  () => import('@/pages/questionnaire/results')
);
const QuestionnaireRespondPage = lazy(
  () => import('@/pages/questionnaire-public/respond')
);
const FlightMapPage = lazy(() => import('@/pages/flight-map'));
const FlightPlanLearningPage = lazy(
  () => import('@/pages/flight-plan-learning')
);
const PresentationPage = lazy(() => import('@/pages/presentation'));
const SuggestedQuestionsPage = lazy(
  () => import('@/pages/suggested-questions')
);
const PromotionsPage = lazy(() => import('@/pages/promotions'));
const UserFirAssignmentsPage = lazy(
  () => import('@/pages/user-fir-assignments')
);
const AtlasFirsPage = lazy(() => import('@/pages/atlas-firs'));
const EmployeesPage = lazy(() => import('@/pages/employees'));
const AirlineFilesPage = lazy(() => import('@/pages/airline-files'));
const AirportNotesPage = lazy(() => import('@/pages/airport-notes'));
const UserAppLogsPage = lazy(() => import('@/pages/user-app-logs'));
const UserSurveysPage = lazy(() => import('@/pages/user-surveys'));
const ApiLogsPage = lazy(() => import('@/pages/api-logs'));

const KbiOverviewPage = lazy(() => import('@/pages/kbi/overview'));
const KbiKpiDetailPage = lazy(() => import('@/pages/kbi/kpi-detail'));
const KbiNegotiationListPage = lazy(
  () => import('@/pages/kbi/negotiations/list')
);
const KbiNegotiationDetailPage = lazy(
  () => import('@/pages/kbi/negotiations/detail')
);
const KbiValidatorListPage = lazy(() => import('@/pages/kbi/validator/list'));
const KbiValidatorDetailPage = lazy(
  () => import('@/pages/kbi/validator/detail')
);
const KbiValidatorSamplesPage = lazy(
  () => import('@/pages/kbi/validator-samples')
);
const KbiBaselineListPage = lazy(() => import('@/pages/kbi/baseline/list'));
const KbiBaselineDetailPage = lazy(() => import('@/pages/kbi/baseline/detail'));
const KbiParticipantsPage = lazy(() => import('@/pages/kbi/participants'));
const KbiAgentVersionListPage = lazy(
  () => import('@/pages/kbi/agent-versions/list')
);
const KbiAgentVersionDetailPage = lazy(
  () => import('@/pages/kbi/agent-versions/detail')
);
const KbiOperationsPage = lazy(() => import('@/pages/kbi/operations'));
const KbiUsageCostPage = lazy(() => import('@/pages/kbi/usage-cost'));
const KbiUsageCostNegotiationPage = lazy(
  () => import('@/pages/kbi/usage-cost/detail')
);

const withSuspense = (Comp: React.LazyExoticComponent<() => JSX.Element>) => (
  <Suspense fallback={<FullScreenSpinner />}>
    <Comp />
  </Suspense>
);

const RequireAuth = ({children}: {children: React.ReactNode}) => {
  const [user, loading] = useAuthState(auth);
  if (loading) return <FullScreenSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
};

/** Shows a "no access" state when the role may not open the page. */
const RequireRole = ({
  allow,
  children
}: {
  allow: (role: TRole) => boolean;
  children: React.ReactNode;
}) => {
  const role = useEmployeeRole();
  if (!allow(role)) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-2 px-8 py-7 text-center">
        <h1 className="text-xl font-semibold text-foreground">No access</h1>
        <p className="text-sm text-muted-foreground">
          Your role does not have permission to open this page.
        </p>
      </div>
    );
  }
  return <>{children}</>;
};

const HomeRedirect = () => {
  const role = useEmployeeRole();
  return <Navigate to={getHomePath(role)} replace />;
};

const withRole = (
  allow: (role: TRole) => boolean,
  Comp: React.LazyExoticComponent<() => JSX.Element>
) => <RequireRole allow={allow}>{withSuspense(Comp)}</RequireRole>;

const routes: RouteObject[] = [
  {
    path: '/login',
    element: <SignInPage />
  },
  {
    path: '/forgot-password',
    element: <ForgotPasswordPage />
  },
  {
    // Public: respondents open the shared link without signing in.
    path: '/q/:token',
    element: withSuspense(QuestionnaireRespondPage)
  },
  {
    element: (
      <RequireAuth>
        {/* One persistent shell so the sidebar (and its scroll position)
            survives navigation instead of remounting with every page. */}
        <Layout>
          <Outlet />
        </Layout>
      </RequireAuth>
    ),
    children: [
      {
        path: '/',
        element: <HomeRedirect />
      },
      {
        path: '/onboarding-admin',
        element: withSuspense(OnboardingAdminPage)
      },
      {
        path: '/airlines',
        element: withSuspense(AirlinesPage)
      },
      {
        path: '/agent-execution',
        element: withSuspense(AgentExecutionListPage)
      },
      {
        path: '/agent-execution/:conversationId',
        element: withSuspense(AgentExecutionDetailPage)
      },
      {
        path: '/tn-agent-execution',
        element: withSuspense(TNAgentExecutionListPage)
      },
      {
        path: '/tn-agent-execution/:eventId',
        element: withSuspense(TNAgentExecutionNegotiationsPage)
      },
      {
        path: '/tn-agent-execution/:eventId/:sessionId',
        element: withSuspense(TNAgentExecutionDetailPage)
      },
      {
        path: '/tn-simulations',
        element: withSuspense(TNSimulationListPage)
      },
      {
        path: '/tn-simulations/:simulationId',
        element: withSuspense(TNSimulationDetailPage)
      },
      {
        path: '/flight-simulator',
        element: withRole(canSimulate, FlightSimulatorListPage)
      },
      {
        path: '/flight-simulator/new',
        element: withRole(canSimulate, FlightSimulatorNewRunPage)
      },
      {
        path: '/flight-simulator/runs/:runId',
        element: withRole(canSimulate, FlightSimulatorRunPage)
      },
      {
        path: '/flight-simulator/guide',
        element: withRole(canSimulate, FlightSimulatorGuidePage)
      },
      {
        path: '/airlab-rcp-scenarios',
        element: withSuspense(RcpScenarioListPage)
      },
      {
        path: '/airlab-rcp-scenarios/new',
        element: withSuspense(RcpScenarioNewPage)
      },
      {
        path: '/airlab-rcp-scenarios/:scenarioId',
        element: withSuspense(RcpScenarioDetailPage)
      },
      {
        path: '/events',
        element: withSuspense(EventListPage)
      },
      {
        path: '/events/:eventId',
        element: withSuspense(EventDetailPage)
      },
      {
        path: '/events/:eventId/map',
        element: withSuspense(FlightMapPage)
      },
      {
        path: '/questionnaires',
        element: withSuspense(QuestionnaireListPage)
      },
      {
        path: '/questionnaires/:questionnaireId',
        element: withSuspense(QuestionnaireDetailPage)
      },
      {
        path: '/questionnaires/:questionnaireId/results',
        element: withSuspense(QuestionnaireResultsPage)
      },
      {
        path: '/flight-plan-learning',
        element: withSuspense(FlightPlanLearningPage)
      },
      {
        path: '/presentation',
        element: withSuspense(PresentationPage)
      },
      {
        path: '/suggested-questions',
        element: withSuspense(SuggestedQuestionsPage)
      },
      {
        path: '/promotions',
        element: withSuspense(PromotionsPage)
      },
      {
        path: '/user-fir-assignments',
        element: withSuspense(UserFirAssignmentsPage)
      },
      {
        path: '/atlas-firs',
        element: withSuspense(AtlasFirsPage)
      },
      {
        path: '/employees',
        element: withRole(canManageEmployees, EmployeesPage)
      },
      {
        path: '/airline-files',
        element: withSuspense(AirlineFilesPage)
      },
      {
        path: '/airport-notes',
        element: withSuspense(AirportNotesPage)
      },
      {
        path: '/user-app-logs',
        element: withSuspense(UserAppLogsPage)
      },
      {
        path: '/user-surveys',
        element: withSuspense(UserSurveysPage)
      },
      {
        path: '/api-logs',
        element: withSuspense(ApiLogsPage)
      },
      {
        path: '/kbi',
        element: withRole(canViewDashboard, KbiOverviewPage)
      },
      {
        path: '/kbi/kpis/:kpi',
        element: withRole(canViewDashboard, KbiKpiDetailPage)
      },
      {
        path: '/kbi/usage-cost',
        element: withRole(canViewDashboard, KbiUsageCostPage)
      },
      {
        path: '/kbi/usage-cost/negotiations/:requestId',
        element: withRole(canViewDashboard, KbiUsageCostNegotiationPage)
      },
      {
        path: '/kbi/negotiations',
        element: withRole(canViewDashboard, KbiNegotiationListPage)
      },
      {
        path: '/kbi/negotiations/:requestId',
        element: withRole(canViewDashboard, KbiNegotiationDetailPage)
      },
      {
        path: '/kbi/validator',
        element: withRole(canValidate, KbiValidatorListPage)
      },
      {
        path: '/kbi/validator/:eventId',
        element: withRole(canValidate, KbiValidatorDetailPage)
      },
      {
        path: '/kbi/validator-samples',
        element: withRole(canDrawSample, KbiValidatorSamplesPage)
      },
      {
        path: '/kbi/baseline',
        element: withRole(canOperate, KbiBaselineListPage)
      },
      {
        path: '/kbi/baseline/:negotiationId',
        element: withRole(canOperate, KbiBaselineDetailPage)
      },
      {
        path: '/kbi/participants',
        element: withRole(canOperate, KbiParticipantsPage)
      },
      {
        path: '/kbi/agent-versions',
        element: withRole(canViewAgentVersions, KbiAgentVersionListPage)
      },
      {
        path: '/kbi/agent-versions/:ref',
        element: withRole(canViewAgentVersions, KbiAgentVersionDetailPage)
      },
      {
        path: '/kbi/operations',
        element: withRole(canOperate, KbiOperationsPage)
      }
    ]
  },
  {
    path: '*',
    element: <NotFoundPage />
  }
];

export const router = createBrowserRouter(routes);
